import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

export default function AdminCaseReview() {
  const [stats, setStats] = useState(null);
  const [volume, setVolume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      const [statsRes, volumeRes] = await Promise.all([
        axios.get(`${API}/admin/case-review/stats?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/case-review/volume?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setStats(statsRes.data);
      setVolume(volumeRes.data);
    } catch (error) {
      toast.error("Failed to load case review data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading case review data...</p>
        </div>
      </div>
    );
  }

  const maxReviews = Math.max(...(volume?.timeline || []).map(t => t.reviews), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Case Review</h1>
          <p className="text-muted-foreground mt-1">Pro/Org feature analytics and monitoring</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="60">Last 60 days</option>
        </select>
      </div>

      {/* Stats KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-muted-foreground text-sm">Total Reviews</p>
            <span className="text-2xl">💼</span>
          </div>
          <p className="text-3xl font-bold text-foreground">{stats?.total_case_reviews || 0}</p>
        </div>
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-muted-foreground text-sm">Recent ({days}d)</p>
            <span className="text-2xl">📈</span>
          </div>
          <p className="text-3xl font-bold text-foreground">{stats?.recent_reviews || 0}</p>
        </div>
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-muted-foreground text-sm">Pro Users</p>
            <span className="text-2xl">👥</span>
          </div>
          <p className="text-3xl font-bold text-purple-400">{stats?.pro_users_using || 0}</p>
        </div>
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-muted-foreground text-sm">Org Users</p>
            <span className="text-2xl">🏢</span>
          </div>
          <p className="text-3xl font-bold text-teal-400">{stats?.org_users_using || 0}</p>
        </div>
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-muted-foreground text-sm">Error Rate</p>
            <span className="text-2xl">⚠️</span>
          </div>
          <p className="text-3xl font-bold text-foreground">{stats?.error_rate || 0}%</p>
        </div>
      </div>

      {/* Volume Chart */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Case Review Volume Trend</h2>
        <div className="flex items-end justify-between gap-1 h-48">
          {volume?.timeline?.map((day, idx) => {
            const height = (day.reviews / maxReviews) * 100;
            return (
              <div key={idx} className="flex-1 flex flex-col items-center justify-end group">
                <div className="relative w-full">
                  <div
                    className="w-full bg-gradient-to-t from-indigo-500 to-indigo-400 rounded-t transition-all hover:from-indigo-400 hover:to-indigo-300"
                    style={{ height: `${Math.max(height, 2)}%` }}
                  />
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-muted px-2 py-1 rounded text-xs text-foreground whitespace-nowrap">
                    {day.reviews} reviews
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-muted-foreground text-sm mt-4">Total: {volume?.total_reviews || 0} case reviews</p>
      </div>

      {/* Feature Adoption */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Premium Feature Adoption</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border border-purple-500/20 p-6 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-foreground font-bold text-lg">Pro Plan</h3>
              <span className="text-3xl">👑</span>
            </div>
            <p className="text-4xl font-bold text-purple-400 mb-2">{stats?.pro_users_using || 0}</p>
            <p className="text-muted-foreground text-sm">users actively using Case Review</p>
          </div>

          <div className="bg-gradient-to-br from-teal-500/10 to-teal-600/10 border border-teal-500/20 p-6 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-foreground font-bold text-lg">Organization Plan</h3>
              <span className="text-3xl">🏢</span>
            </div>
            <p className="text-4xl font-bold text-teal-400 mb-2">{stats?.org_users_using || 0}</p>
            <p className="text-muted-foreground text-sm">organizations using Case Review</p>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
            <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-blue-400 font-semibold mb-2">About Case Review</h3>
            <p className="text-muted-foreground text-sm">
              Case Review is a premium feature available to Pro and Organization plan users. It provides comprehensive grievance analysis, violation assessment, and draft response generation based on uploaded CBAs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
